import { Component, OnInit, ViewChild } from '@angular/core';
import { MatLegacyPaginator as MatPaginator } from '@angular/material/legacy-paginator';
import moment from 'moment';
import { GeneralHelper } from 'src/app/helpers/general-helper';
import { DiscountGroup } from 'src/app/model/company/company';
import CompanySimple from 'src/app/model/company/company-simple';
import IPBoardPlacementType, {
  IPBoardPlacementTypeChoices,
  IPBoardPlacementTypeChoicesArray,
} from 'src/app/model/product/ip-board/ip-board-placement-type';
import {
  JobOverviewClient,
  JobOverviewControllerRequestService,
} from 'src/app/services/api/immo/job-overview/job-overview-controller-request.service';
import { PAGE_SIZE_OPTIONS } from 'src/app/services/page-index-service';
import { RequestService } from 'src/app/services/request.service';
import { SnackService } from 'src/app/services/snack.service';
import { SubscriptionBasedComponent } from 'src/app/subscription-helpers/subscription-based.component';
import { ClientStatus } from './client-status';

@Component({
  selector: 'immo-job-client-overview',
  templateUrl: './immo-job-client-overview.component.html',
  styleUrls: ['./immo-job-client-overview.component.scss'],
})
export class ImmoJobClientOverviewComponent
  extends SubscriptionBasedComponent
  implements OnInit
{
  displayedColumns: string[] = [
    'company',
    'year',
    'discountGroup',
    'total',
    'totalPreviousYear',
    'lastOrderDate',
  ];
  data: JobOverviewClient[] = [];
  totalItems = 0;
  loadingResults = true;
  pageSizeOptions = PAGE_SIZE_OPTIONS;

  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;

  yearOptions: number[] = Array.apply(null, Array(moment().year() - 2018)).map(
    function (x, i) {
      return 2019 + i;
    }
  );
  selectedYear: number = moment().year();
  previousYear: number = moment().year() - 1;

  loadingCompanies: boolean = true;
  companyOptions: CompanySimple[];
  allCompaniesOption: CompanySimple = new CompanySimple(null, 'Alle bedrijven');
  selectedCompanyId: number | null = this.allCompaniesOption.id;
  searchTerm: string | CompanySimple | null = null;
  filteredCompanyOptions: CompanySimple[];
  noCompanySelected: boolean = false;
  searchtTermClearPerformed: boolean = false;

  placementTypeOptions = IPBoardPlacementTypeChoicesArray;
  allPlacementOption: IPBoardPlacementType = new IPBoardPlacementType(
    'Alle',
    null
  );
  selectedPlacementType: IPBoardPlacementType = this.allPlacementOption;

  discountGroupOptions = Object.values(DiscountGroup);
  allDiscountGroupOptioinsSelected: boolean = true;
  selectedDiscountGroups: string[] = this.discountGroupOptions;
  clientStatusOptions = Object.values(ClientStatus);
  selectedClientStatus: string;

  constructor(
    private requestService: RequestService,
    private snackService: SnackService,
    private jobOverviewService: JobOverviewControllerRequestService
  ) {
    super();
  }

  ngOnInit(): void {
    this.paginator.pageIndex = 0;
    this.paginator.pageSize = 30;

    this.addSubscription(this.paginator.page, () => this.getOverview());
    this.selectedClientStatus = ClientStatus.ACTIVE.valueOf();
    this.getCompanies();
    this.getOverview();
  }

  private getCompanies(): void {
    this.addSubscription(
      this.requestService.get('/companies/unpaged-simple'),
      (result: any) => {
        this.companyOptions = result.body;
        this.filteredCompanyOptions = this.companyOptions;
        this.loadingCompanies = false;
      },
      (err) => {
        if (err instanceof Error) {
          this.snackService.showError(err.message);
        } else {
          this.snackService.showError(err);
        }
      }
    );
  }

  async getOverview(resetPageIndex: boolean = false): Promise<void> {
    if (resetPageIndex) {
      this.paginator.pageIndex = 0;
    }
    this.jobOverviewService
      .getJobsOverviewClient(
        this.paginator.pageIndex,
        this.paginator.pageSize,
        this.selectedYear,
        this.selectedDiscountGroups,
        this.findValueForSelectedClientStatus(),
        this.selectedCompanyId,
        this.selectedPlacementType.ordinal != null
          ? Object.entries(IPBoardPlacementTypeChoices).find(
              (e) => e[1].ordinal === this.selectedPlacementType.ordinal
            )[0]
          : null
      )
      .then((res) => {
        this.data = res.items;
        this.totalItems = res.totalItems;
      })
      .catch(() => {
        this.snackService.showError(
          'Ophalen locaties mislukt. Herlaad de pagina'
        );
      })
      .finally(() => {
        this.loadingResults = false;
      });
  }

  filterOverview() {
    if (this.searchTerm != null && typeof this.searchTerm === 'string') {
      this.noCompanySelected = true;
      this.snackService.showWarning(
        'Gelieve een optie te selecteren in de bedrijven filter'
      );
    } else {
      this.noCompanySelected = false;
      this.getOverview(true);
    }
  }

  getDiscountGroupDisplay(): string {
    if (
      this.selectedDiscountGroups == null ||
      this.allDiscountGroupOptioinsSelected ||
      this.selectedDiscountGroups.join(',') ===
        this.discountGroupOptions.join(',')
    ) {
      return 'Alle kortingsgroepen';
    } else {
      return this.selectedDiscountGroups.join(', ');
    }
  }

  onDiscountGroupSelectionChanged() {
    this.allDiscountGroupOptioinsSelected =
      this.selectedDiscountGroups.join(',') ===
      this.discountGroupOptions.join(',');
  }

  onDiscountGroupAllChanged() {
    if (this.allDiscountGroupOptioinsSelected) {
      this.selectedDiscountGroups = this.discountGroupOptions;
    } else {
      this.selectedDiscountGroups = [];
    }
  }

  onSearchTermChanged() {
    if (this.searchTerm != null && typeof this.searchTerm === 'string') {
      this.filteredCompanyOptions = this.companyOptions.filter((option) =>
        option.regularName
          .toLowerCase()
          .includes((this.searchTerm as string).toLowerCase())
      );
    } else {
      this.noCompanySelected = false;
      this.filteredCompanyOptions = this.companyOptions;
    }
  }

  clearSearchTermAll() {
    if (
      this.searchTerm === this.allCompaniesOption ||
      this.searchTerm == null
    ) {
      this.searchTerm = '';
      this.searchtTermClearPerformed = true;
    }
  }

  resetSearchTermAll() {
    if (this.searchtTermClearPerformed) {
      this.searchtTermClearPerformed = false;
    }
  }

  onSelectCompany(company: CompanySimple) {
    this.selectedCompanyId = company.id;
  }

  displayCompanyFn(company: CompanySimple): string {
    return company == null || company == this.allCompaniesOption
      ? 'Alle bedrijven'
      : company.regularName
      ? company.regularName
      : '';
  }

  compareOrdinalEnum(o1: any, o2: any): boolean {
    return GeneralHelper.compareOrdinalEnum(o1, o2);
  }

  compareEntities(o1: any, o2: any): boolean {
    return GeneralHelper.compareEntities(o1, o2);
  }

  trackById(index: number, obj: any): number {
    return GeneralHelper.trackById(index, obj);
  }

  findValueForSelectedClientStatus() {
    let result = Object.entries(ClientStatus).find(
      (value) => value[1] === this.selectedClientStatus
    );
    return result[0];
  }
}
